import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { UserFragment } from 'graphql/fragment/user.fragment'
import { MeetingAgendaFragment } from 'graphql/meeting/agenda/meeting.agenda.fragment'
import { MeetingAnnotationFragment } from 'graphql/meeting/annotation/meeting.annotation.fragment'
import { MeetingFragment } from 'graphql/meeting/meeting.fragment'
import {
  MeetingParticipantFragment,
} from 'graphql/meeting/participant/meeting.participant.fragment'
import { MeetingRecordFragment } from 'graphql/meeting/record/meeting.record.fragment'
import { MeetingSummaryFragment } from 'graphql/meeting/summary/meeting.summary.fragment'
import { equalId, ID } from 'utils/id'

export type Agenda = {
  id: ID
  name: string
  description?: string
  duration?: number
  updated: string
  created: string
  idx: number
  authorId?: ID
}
export type AgendaMap = {
  [key: ID]: Agenda
}

export type Participant = {
  id: ID
  email: string
  sent: string | null
  role: string
  created: string
  updated: string
  avatar: string
  attendance: string | null
}
export type ParticipantMap = {
  [key: ID]: Participant
}

export type Author = {
  id: ID
  name?: string
  avatar?: string
}
export type AuthorMap = {
  [key: ID]: Author
}

export type MeetingRecord = {
  id: ID
  idx: number
  duration?: number
  /** Absent when the meeting record has no record. */
  status?: string
  recordId?: ID
  transcriptId?: ID
  restrictToPreview?: boolean
}
export type RecordMap = {
  [key: ID]: MeetingRecord
}

export type Annotation = {
  id: ID
  type: string
  annotatedText: string
  offsetInWords: number
  comment?: string
  todoDone?: string
  paragraphId: ID
  recordId: ID
  creatorId: ID
  agendaId?: ID
  agendaIdOfParagraph?: ID
}
export type AnnotationMap = {
  [key: ID]: Annotation
}

export type Summary = {
  id: ID
  agendaId?: ID
  authorId: ID
  content: string
  created: string
  updated: string
}
export type SummaryMap = {
  [key: ID]: Summary
}
export type MeetingState = {
  id?: ID
  startTime?: string
  endTime?: string
  name?: string
  bot?: boolean
  description?: string
  language?: string
  external?: boolean
  type?: ID

  canCollectRecords?: boolean
  canChangeTime?: boolean
  canChangeCall?: boolean
  canChangeDescription?: boolean
  canChangeName?: boolean
  canChangeParticipant?: boolean

  agendaIds: ID[]
  participantIds: ID[]
  recordIds: ID[]
  annotationIds: ID[]
  summaryIds: ID[]
  authorId?: ID
  companyId?: ID

  agenda: AgendaMap
  participants: ParticipantMap
  authors: AuthorMap
  records: RecordMap
  annotations: AnnotationMap
  summaries: SummaryMap
  previousLocation?: string
}

const initialState: MeetingState = {
  agenda: {},
  agendaIds: [],
  annotationIds: [],
  annotations: {},
  authorId: undefined,
  authors: {},
  bot: undefined,
  canChangeCall: undefined,
  canChangeName: undefined,
  canChangeParticipant: undefined,
  canChangeTime: undefined,
  canCollectRecords: undefined,
  companyId: undefined,
  description: undefined,
  endTime: undefined,
  external: undefined,
  id: undefined,
  language: undefined,
  name: undefined,
  participantIds: [],
  participants: {},
  previousLocation: undefined,
  recordIds: [],
  records: {},
  startTime: undefined,
  summaries: {},
  summaryIds: [],
  type: undefined,
}

export const meetingSlice = createSlice({
  initialState,
  name: 'meeting',
  reducers: {
    addAgenda: (state, action: PayloadAction<MeetingAgendaFragment>) => {
      if (state.agendaIds.includes(action.payload.id)) {
        throw new Error('agenda already present')
      }

      state.agendaIds.push(action.payload.id)
      state.agenda[action.payload.id] = agendaFragmentToImmutable(action.payload)
      if (action.payload.author) {
        state.authors[action.payload.author.id] = userFragmentToAuthor(action.payload.author)
      }
      return state
    },
    addAnnotation: (state, action: PayloadAction<MeetingAnnotationFragment>) => {
      if (state.annotationIds.includes(action.payload.id)) {
        throw new Error('annotation already present')
      }

      state.annotationIds.push(action.payload.id)
      state.annotations[action.payload.id] = annotationFragmentToAnnotation(action.payload)

      if (action.payload.creator) {
        state.authors[action.payload.creator.id] = userFragmentToAuthor(action.payload.creator)
      }

      return state
    },
    addParticipant: (state, action: PayloadAction<MeetingParticipantFragment>) => {
      if (state.participantIds.includes(action.payload.id)) {
        throw new Error('participant already present')
      }

      state.participantIds.push(action.payload.id)
      state.participants[action.payload.id] = participantFragmentToParticipant(action.payload)
      return state
    },
    addRecord: (state, action: PayloadAction<MeetingRecordFragment>) => {
      if (state.recordIds.includes(action.payload.id)) {
        throw new Error('record already present')
      }

      state.recordIds.push(action.payload.id)
      state.records[action.payload.id] = meetingRecordFragmentToMeetingRecord(action.payload)
      return state
    },
    addSummary: (state, action: PayloadAction<MeetingSummaryFragment>) => {
      if (state.summaryIds.includes(action.payload.id)) {
        throw new Error('summary already present')
      }

      state.summaryIds.push(action.payload.id)
      state.summaries[action.payload.id] = summaryFragmentToSummary(action.payload)
      if (action.payload.author) {
        state.authors[action.payload.author.id] = userFragmentToAuthor(action.payload.author)
      }
      return state
    },
    deleteAgenda: (state, action: PayloadAction<any>) => {
      state.agendaIds = state.agendaIds.filter((id) => !equalId(id, action.payload))
      return state
    },
    deleteAnnotation: (state, action: PayloadAction<ID>) => {
      state.annotationIds = state.annotationIds.filter((id) => !equalId(id, action.payload))
      delete state.annotations[action.payload]
      return state
    },
    deleteParticipant: (state, action: PayloadAction<ID>) => {
      state.participantIds = state.participantIds.filter((id) => !equalId(id, action.payload))
      return state
    },
    deleteRecord: (state, action: PayloadAction<ID>) => {
      state.recordIds = state.recordIds.filter((id) => !equalId(id, action.payload))
      delete state.records[action.payload]

      state.annotationIds = state.annotationIds.filter(
        (annotationId) => {
          const toDelete = equalId(
            state.annotations[annotationId].recordId,
            action.payload,
          )

          if (toDelete) {
            delete state.annotations[annotationId]
          }

          return !toDelete
        },
      )

      return state
    },
    deleteSummary: (state, action: PayloadAction<ID>) => {
      state.summaryIds = state.summaryIds.filter((id) => !equalId(id, action.payload))
      delete state.summaries[action.payload]
      return state
    },
    /** replaces the agenda list with the given entries, which are the complete agenda of a schedule or instance */
    setAgenda: (state, action: PayloadAction<MeetingAgendaFragment[]>) => {
      state.agendaIds = action.payload.map((agenda) => agenda.id)

      action.payload.forEach((agenda) => {
        state.agenda[agenda.id] = agendaFragmentToImmutable(agenda)

        if (agenda.author && !state.authors[agenda.author.id]) {
          state.authors[agenda.author.id] = userFragmentToAuthor(agenda.author)
        }
      })

      return state
    },
    setAnnotations: (state, action: PayloadAction<MeetingAnnotationFragment[]>) => {
      action.payload.forEach((annotation) => {
        if (!state.participantIds.includes(annotation.id)) {
          state.annotationIds.push(annotation.id)
        }

        state.annotations[annotation.id] = annotationFragmentToAnnotation(annotation)

        if (annotation.creator && !state.authors[annotation.creator.id]) {
          state.authors[annotation.creator.id] = userFragmentToAuthor(annotation.creator)
        }
      })

      return state
    },
    setMeeting: (state, action: PayloadAction<MeetingFragment>) => {
      const meeting = action.payload

      if (!equalId(state.id, meeting.id)) {
        state.agendaIds = []
        state.participantIds = []
        state.recordIds = []
      }

      Object.assign(state, {
        author: userFragmentToAuthor(meeting.author),
        authorId: meeting.author.id,
        bot: meeting.bot,
        canChangeCall: meeting.canChangeCall,
        canChangeDescription: meeting.canChangeDescription,
        canChangeName: meeting.canChangeName,
        canChangeParticipant: meeting.canChangeParticipant,
        canChangeTime: meeting.canChangeTime,
        canCollectRecords: meeting.canCollectRecords,
        companyId: meeting.company?.id,
        description: meeting.description,
        endTime: meeting.endTime,
        external: meeting.external,
        id: meeting.id,
        language: meeting.language,
        meetingType: meeting.type?.id,
        name: meeting.name,
        startTime: meeting.startTime,
      })

      return state
    },
    /** replaces the participant list with the given entries, which are the complete participants of a schedule */
    setParticipants: (state, action: PayloadAction<MeetingParticipantFragment[]>) => {
      state.participantIds = action.payload.map((participant) => participant.id)

      action.payload.forEach((participant) => {
        state.participants[participant.id] = participantFragmentToParticipant(participant)
      })

      return state
    },
    setPreviousLocation: (state, action: PayloadAction<string>) => {
      state.previousLocation = action.payload
      return state
    },
    setRecords: (state, action: PayloadAction<MeetingRecordFragment[]>) => {
      action.payload.forEach((record) => {
        if (!state.recordIds.includes(record.id)) {
          state.recordIds.push(record.id)
        }

        state.records[record.id] = meetingRecordFragmentToMeetingRecord(record)
      })
      return state
    },
    setSummary: (state, action: PayloadAction<MeetingSummaryFragment[]>) => {
      action.payload.forEach((summary) => {
        if (!state.summaryIds.includes(summary.id)) {
          state.summaryIds.push(summary.id)
        }

        state.summaries[summary.id] = summaryFragmentToSummary(summary)

        if (summary.author && !state.authors[summary.author.id]) {
          state.authors[summary.author.id] = userFragmentToAuthor(summary.author)
        }
      })

      return state
    },
    updateAgenda: (
      state,
      action: PayloadAction<{ id: ID; agenda: Partial<MeetingAgendaFragment> }>,
    ) => {
      if (action.payload.agenda.idx !== undefined) {
        state.agenda[action.payload.id].idx = action.payload.agenda.idx
      }

      if (action.payload.agenda.name !== undefined) {
        state.agenda[action.payload.id].name = action.payload.agenda.name
      }

      if (action.payload.agenda.duration !== undefined) {
        state.agenda[action.payload.id].duration = action.payload.agenda.duration ?? undefined
      }

      if (action.payload.agenda.description !== undefined) {
        state.agenda[action.payload.id].description = action.payload.agenda.description ?? undefined
      }

      if (action.payload.agenda.updated !== undefined) {
        state.agenda[action.payload.id].updated = action.payload.agenda.updated
      }
      return state
    },
    updateAnnotation: (
      state,
      action: PayloadAction<{id: ID; annotation: Partial<MeetingAnnotationFragment>}>,
    ) => {
      if (action.payload.annotation.comment !== undefined) {
        state.annotations[action.payload.id].comment = action.payload.annotation.comment ?? undefined
      }

      if (action.payload.annotation.type !== undefined) {
        state.annotations[action.payload.id].type = action.payload.annotation.type
      }

      if (action.payload.annotation.todoDone !== undefined) {
        state.annotations[action.payload.id].todoDone = action.payload.annotation.todoDone ?? undefined
      }

      if (action.payload.annotation.annotatedText !== undefined) {
        state.annotations[action.payload.id].annotatedText = action.payload.annotation.annotatedText
      }

      if (action.payload.annotation.offsetInWords !== undefined) {
        state.annotations[action.payload.id].offsetInWords = action.payload.annotation.offsetInWords
      }

      if (action.payload.annotation.agenda?.id !== undefined) {
        state.annotations[action.payload.id].agendaId = action.payload.annotation.agenda.id
      }

      return state
    },
    updateMeeting: (state, action: PayloadAction<MeetingFragment>) => {
      if (action.payload.bot !== undefined) {
        state.bot = action.payload.bot
      }
      if (action.payload.canChangeCall !== undefined) {
        state.canChangeCall = action.payload.canChangeCall
      }
      if (action.payload.canChangeDescription !== undefined) {
        state.canChangeDescription = action.payload.canChangeDescription
      }
      if (action.payload.canChangeName !== undefined) {
        state.canChangeName = action.payload.canChangeName
      }
      if (action.payload.canChangeParticipant !== undefined) {
        state.canChangeParticipant = action.payload.canChangeParticipant
      }
      if (action.payload.canChangeTime !== undefined) {
        state.canChangeTime = action.payload.canChangeTime
      }
      if (action.payload.canCollectRecords !== undefined) {
        state.canCollectRecords = action.payload.canCollectRecords
      }
      if (action.payload.description !== undefined) {
        state.description = action.payload.description ?? undefined
      }
      if (action.payload.endTime !== undefined) {
        state.endTime = action.payload.endTime ?? undefined
      }
      if (action.payload.external !== undefined) {
        state.external = action.payload.external
      }
      if (action.payload.language !== undefined) {
        state.language = action.payload.language ?? undefined
      }
      if (action.payload.name !== undefined) {
        state.name = action.payload.name ?? undefined
      }
      if (action.payload.startTime !== undefined) {
        state.startTime = action.payload.startTime ?? undefined
      }

      return state
    },
    updateParticipant: (
      state,
      action: PayloadAction<{id: ID; participant: Partial<MeetingParticipantFragment>}>,
    ) => {

      if (
        action.payload.participant.sent !== undefined
        && !state.participants[action.payload.id].sent
      ) {
        state.participants[action.payload.id].sent = action.payload.participant.sent
      }

      if (action.payload.participant.role !== undefined) {
        state.participants[action.payload.id].role = action.payload.participant.role
      }

      if (action.payload.participant.updated !== undefined) {
        state.participants[action.payload.id].updated = action.payload.participant.updated
      }

      if (action.payload.participant.attendance !== undefined) {
        state.participants[action.payload.id].attendance = action.payload.participant.attendance
      }

      return state
    },
    updateRecord: (
      state,
      action: PayloadAction<{id: ID; record: Partial<MeetingRecordFragment>}>,
    ) => {
      const status = action.payload.record.record?.status
      if (status !== undefined) {
        state.records[action.payload.id].status = status
      }
      return state
    },
    updateSummary: (
      state,
      action: PayloadAction<{id: ID; summary: Partial<MeetingSummaryFragment>}>,
    ) => {
      if (action.payload.summary.content !== undefined) {
        state.summaries[action.payload.id].content = action.payload.summary.content
      }

      if (action.payload.summary.agenda !== undefined) {
        state.summaries[action.payload.id].agendaId = action.payload.summary.agenda?.id
      }
      return state
    },
  },
})

export const {
  setMeeting,
  updateMeeting,
  addParticipant,
  updateParticipant,
  deleteParticipant,
  setParticipants,
  addRecord,
  updateRecord,
  deleteRecord,
  setRecords,
  addAnnotation,
  updateAnnotation,
  deleteAnnotation,
  setAnnotations,
  setSummary,
  addSummary,
  updateSummary,
  deleteSummary,
  setAgenda,
  addAgenda,
  updateAgenda,
  deleteAgenda,
  setPreviousLocation,
} = meetingSlice.actions

const userFragmentToAuthor = (user: UserFragment) => {
  return {
    avatar: user.avatar,
    id: user.id,
    name: user.name,
  } as Author
}

const participantFragmentToParticipant = (participant: MeetingParticipantFragment) => {
  return {
    attendance: participant.attendance,
    avatar: participant.avatar,
    created: participant.created,
    email: participant.email,
    id: participant.id,
    role: participant.role,
    sent: participant.sent,
    updated: participant.updated,
  } as Participant
}

const meetingRecordFragmentToMeetingRecord = (record: MeetingRecordFragment): MeetingRecord => {
  return {
    duration: record.record?.duration ?? undefined,
    id: record.id,
    idx: record.idx,
    recordId: record.record?.id,
    restrictToPreview: record.record?.restrictToPreview,
    status: record.record?.status,
    transcriptId: record.record?.transcript?.id,
  }
}

const annotationFragmentToAnnotation = (annotation: MeetingAnnotationFragment) => {
  return {
    agendaId: annotation.agenda?.id,
    agendaIdOfParagraph: annotation.paragraph.agenda?.id,
    annotatedText: annotation.annotatedText,
    comment: annotation.comment,
    creatorId: annotation.creator?.id,
    id: annotation.id,
    offsetInWords: annotation.offsetInWords,
    paragraphId: annotation.paragraph.id,
    recordId: annotation.record.id,
    todoDone: annotation.todoDone,
    type: annotation.type,
  } as Annotation
}

const summaryFragmentToSummary = (summary: MeetingSummaryFragment) => {
  return {
    agendaId: summary.agenda?.id,
    authorId: summary.author?.id,
    content: summary.content,
    created: summary.created,
    id: summary.id,
    updated: summary.updated,
  } as Summary
}

const agendaFragmentToImmutable = (agenda: MeetingAgendaFragment) => {
  return {
    created: agenda.created,
    description: agenda.description,
    duration: agenda.duration,
    id: agenda.id,
    idx: agenda.idx,
    name: agenda.name,
    updated: agenda.updated,
  } as Agenda
}
